import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import {
  Briefcase,
  MapPin,
  Clock,
  ArrowRight,
  Home,
  TrendingUp,
  DollarSign,
  Heart,
  Plane,
  Laptop,
  Users,
  CheckCircle2,
} from 'lucide-react';
import { cn } from '@/lib/cn';

export function CareersPage() {
  const { t } = useTranslation();

  const benefits = [
    { key: 'remote', icon: Home, color: 'from-blue-500 to-cyan-500' },
    { key: 'growth', icon: TrendingUp, color: 'from-green-500 to-emerald-500' },
    { key: 'equity', icon: DollarSign, color: 'from-purple-500 to-violet-500' },
    { key: 'health', icon: Heart, color: 'from-red-500 to-rose-500' },
    { key: 'vacation', icon: Plane, color: 'from-orange-500 to-amber-500' },
    { key: 'equipment', icon: Laptop, color: 'from-indigo-500 to-blue-500' },
  ];

  const positions = [0, 1, 2, 3, 4];
  const processSteps = [0, 1, 2, 3, 4];

  return (
    <>
      <SEO {...SEO_CONFIG.careers} />
      <div className="min-h-screen">
      {/* Hero */}
      <section className="section-lg relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />

        <div className="container relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm mb-6">
              <Briefcase size={16} />
              <span>{t('careers.badge')}</span>
            </div>

            <h1 className="heading-xl mb-6">
              {t('careers.title')}{' '}
              <span className="text-accent">{t('careers.titleHighlight')}</span>
            </h1>
            <p className="text-lg text-text-secondary">
              {t('careers.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">{t('careers.whyJoin.title')}</h2>
            <p className="text-text-secondary">{t('careers.whyJoin.subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit) => (
              <div key={benefit.key} className="card p-6 hover:border-accent/30 transition-colors">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${benefit.color} flex items-center justify-center mb-4`}>
                  <benefit.icon className="text-white" size={24} />
                </div>
                <h3 className="font-semibold mb-2">{t(`careers.whyJoin.benefits.${benefit.key}.title`)}</h3>
                <p className="text-text-secondary text-sm">{t(`careers.whyJoin.benefits.${benefit.key}.description`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Culture */}
      <section className="section">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <Users className="mx-auto mb-6 text-accent" size={48} />
            <h2 className="heading-lg mb-4">{t('careers.culture.title')}</h2>
            <p className="text-text-secondary text-lg">
              {t('careers.culture.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">{t('careers.openPositions.title')}</h2>
            <p className="text-text-secondary">{t('careers.openPositions.subtitle')}</p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {positions.map((index) => (
              <div key={index} className="card p-6 hover:border-accent/30 transition-colors group">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-lg group-hover:text-accent transition-colors">
                      {t(`careers.openPositions.positions.${index}.title`)}
                    </h3>
                    <div className="flex flex-wrap gap-3 mt-2">
                      <span className="inline-flex items-center gap-1 text-sm text-text-secondary">
                        <Briefcase size={14} />
                        {t(`careers.openPositions.positions.${index}.department`)}
                      </span>
                      <span className="inline-flex items-center gap-1 text-sm text-text-secondary">
                        <MapPin size={14} />
                        {t(`careers.openPositions.positions.${index}.location`)}
                      </span>
                      <span className="inline-flex items-center gap-1 text-sm text-text-secondary">
                        <Clock size={14} />
                        {t(`careers.openPositions.positions.${index}.type`)}
                      </span>
                    </div>
                  </div>
                  <Link
                    to={`/contact?position=${encodeURIComponent(t(`careers.openPositions.positions.${index}.title`))}`}
                    className="btn btn-primary btn-sm whitespace-nowrap"
                  >
                    {t('careers.openPositions.applyNow')}
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">{t('careers.process.title')}</h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-5 gap-4">
              {processSteps.map((index) => (
                <div key={index} className="relative">
                  <div className="text-center">
                    <div className={cn(
                      'w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3',
                      'bg-gradient-to-br from-accent to-purple-600 text-white font-bold'
                    )}>
                      {index + 1}
                    </div>
                    <h3 className="font-semibold text-sm mb-1">{t(`careers.process.steps.${index}.title`)}</h3>
                    <p className="text-text-tertiary text-xs">{t(`careers.process.steps.${index}.description`)}</p>
                  </div>
                  {index < 4 && (
                    <div className="hidden md:block absolute top-6 left-[60%] w-[80%] h-px bg-border-default" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <div className="card p-8 md:p-12 bg-gradient-to-br from-accent/10 to-purple-500/5 border-accent/20">
              <h2 className="heading-lg mb-4">{t('careers.cta.title')}</h2>
              <p className="text-text-secondary mb-8">{t('careers.cta.subtitle')}</p>
              <Link to="/contact" className="btn btn-primary">
                {t('careers.cta.contact')}
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
